import { Component, Inject, OnInit } from "@angular/core";
import { HttpErrorResponse } from "@angular/common/http";
import {
  FormBuilder,
  FormGroup,
  Validators,
  AbstractControl,
  UntypedFormControl,
} from "@angular/forms";
import {
  MatDialog,
  MatDialogRef,
  MAT_DIALOG_DATA,
} from "@angular/material/dialog";
import { TranslateService } from "@ngx-translate/core";
import { validateHTTPExists } from "../../shared/validators/url.validator";
import { ClientService } from "../client.service";
import { ToastrService } from "../../shared/toastr.service";
import { LimitPlanDialogComponent } from "../../shared/limit-plan-dialog/limit-plan-dialog.component";
import { ShowOnSubmitAttemptErrorStateMatcher } from "../../shared/error-state-matcher";
import {
  AddSubdomainDialogData,
  AddSubdomainRequest,
} from "../client.interface";

@Component({
  selector: "app-add-subdomain-dialog",
  templateUrl: "./add-subdomain-dialog.component.html",
  styleUrls: ["./add-subdomain-dialog.component.scss"],
  standalone: false,
})
export class AddSubdomainDialogComponent implements OnInit {
  form: FormGroup;
  loading = false;
  errorStateMatcher = new ShowOnSubmitAttemptErrorStateMatcher();

  constructor(
    private fb: FormBuilder,
    private dialogRef: MatDialogRef<AddSubdomainDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: AddSubdomainDialogData,
    private translate: TranslateService,
    private clientService: ClientService,
    private toastrService: ToastrService,
    private dialog: MatDialog,
  ) {
    this.form = this.fb.group({
      subdomain: [
        "",
        [
          (c: AbstractControl) => Validators.required(c),
          (c: AbstractControl) => validateHTTPExists(c as UntypedFormControl),
        ],
      ],
      googleAdminEmail: ["", (c: AbstractControl) => Validators.required(c)],
      trialFinishDate: [null],
    });
  }

  ngOnInit(): void {
    if (this.data.planId === "free") {
      this.form
        .get("trialFinishDate")
        ?.setValidators([(c) => Validators.required(c)]);
    } else {
      this.form.get("trialFinishDate")?.clearValidators();
    }
    this.form.get("trialFinishDate")?.updateValueAndValidity();
  }

  onSubmit(): void {
    this.errorStateMatcher.submitAttempted = true;
    if (this.form.invalid || this.loading) {
      return;
    }

    this.loading = true;
    const formValue = this.form.value as AddSubdomainRequest;

    const request: AddSubdomainRequest = {
      subdomain: formValue.subdomain,
      googleAdminEmail: formValue.googleAdminEmail,
      trialFinishDate: formValue.trialFinishDate,
    };

    this.clientService.addSubdomain(this.data.clientId, request).subscribe({
      next: () => {
        this.loading = false;
        this.toastrService.success(
          this.translate.instant("client.subdomainAddedSuccess") as string,
        );
        this.dialogRef.close(true);
      },
      error: (error: HttpErrorResponse) => {
        this.loading = false;
        const e = error as {
          error?: { message?: string } | string;
          message?: string;
        };
        const errorMessage =
          (typeof e.error === "object" && e.error !== null
            ? e.error.message
            : typeof e.error === "string"
              ? e.error
              : "") ||
          e.message ||
          "";
        if (
          typeof errorMessage === "string" &&
          errorMessage.includes("SUBDOMAIN_LIMIT_REACHED")
        ) {
          this.dialogRef.close();
          this.dialog.open(LimitPlanDialogComponent, {
            width: "500px",
            maxWidth: "90vw",
            data: { plan: "basic", type: "subdomain" },
          });
        } else {
          this.toastrService.error(
            this.translate.instant("client.subdomainAddError") as string,
          );
        }
      },
    });
  }

  onCancel(): void {
    this.dialogRef.close();
  }
}
